import React, { useEffect, useMemo, useState } from 'react';
import SpecsNote from '../../components/common/SpecsNote';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { ArrowRight, Clock, Calendar, Lightbulb, Plus, MapPin, MessageCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { slugify, formatPrice } from '../../utils';
import { getPost } from '../../content/blog';
import seo from '../../seo/seoConfig.json';
import NotFound from '../NotFound';
import TitleText from '../../components/common/TitleText';

const WHATSAPP = 'https://wa.me/18297163555?text=';

// Renders [text](/path/) links inside paragraphs; internal paths use the router.
const RichText = ({ text }) => {
    const parts = [];
    const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
    let last = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > last) parts.push(text.slice(last, match.index));
        if (match[3]) {
            parts.push(<strong key={match.index} className="font-bold text-black">{match[3]}</strong>);
        } else if (match[2].startsWith('/')) {
            parts.push(<Link key={match.index} to={match[2]} className="font-semibold text-black underline decoration-2 underline-offset-4 decoration-zinc-300 hover:decoration-black">{match[1]}</Link>);
        } else {
            parts.push(<a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer" className="font-semibold text-black underline decoration-2 underline-offset-4 decoration-zinc-300 hover:decoration-black">{match[1]}</a>);
        }
        last = regex.lastIndex;
    }
    if (last < text.length) parts.push(text.slice(last));
    return <>{parts}</>;
};

const headingId = (text) => slugify(text).slice(0, 60);

const ProductsBlock = ({ block }) => {
    const [bikes, setBikes] = useState([]);
    useEffect(() => {
        supabase.from('bicicletas').select('id, modelo, precio_eur, imagenes_urls').in('modelo', block.models)
            .then(({ data }) => setBikes((data || []).sort((a, b) => block.models.indexOf(a.modelo) - block.models.indexOf(b.modelo))));
    }, [block.models]);
    if (!bikes.length) return null;
    return (
        <div className="my-12 not-prose">
            {block.title && <h3 className="text-sm font-black uppercase tracking-[0.2em] text-gray-500 mb-5">{block.title}</h3>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {bikes.map((bike) => (
                    <Link key={bike.id} to={`/product/${slugify(bike.modelo)}`} className="group block rounded-2xl border border-zinc-200 p-5 hover:border-black transition-colors">
                        <div className="aspect-[4/3] flex items-center justify-center mb-4">
                            {bike.imagenes_urls?.[0] && <img loading="lazy" decoding="async" src={bike.imagenes_urls[0]} alt={`Raymon ${bike.modelo}`} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500" />}
                        </div>
                        <div className="font-black uppercase tracking-tight">Raymon {bike.modelo}</div>
                        <div className="text-sm text-gray-500">{formatPrice(bike.precio_eur, 'bikes')}</div>
                    </Link>
                ))}
            </div>
        </div>
    );
};

const Block = ({ block }) => {
    switch (block.type) {
        case 'h2':
            return <h2 id={headingId(block.text)} className="scroll-mt-28 text-3xl md:text-4xl font-black italic tracking-tight text-black mt-16 mb-6 leading-tight">{block.text}</h2>;
        case 'h3':
            return <h3 className="text-xl md:text-2xl font-black italic text-black mt-10 mb-4">{block.text}</h3>;
        case 'p':
            return <p className="text-lg leading-relaxed text-gray-700 mb-6"><RichText text={block.text} /></p>;
        case 'ul':
            return <ul className="mb-8 space-y-3">{block.items.map((item) => <li key={item} className="flex gap-3 text-lg text-gray-700 leading-relaxed"><span className="mt-3 w-2 h-2 rounded-full bg-black shrink-0" /><span><RichText text={item} /></span></li>)}</ul>;
        case 'ol':
            return <ol className="mb-8 space-y-4">{block.items.map((item, i) => <li key={item} className="flex gap-4 text-lg text-gray-700 leading-relaxed"><span className="w-8 h-8 rounded-full bg-black text-white text-sm font-black flex items-center justify-center shrink-0">{i + 1}</span><span className="pt-0.5"><RichText text={item} /></span></li>)}</ol>;
        case 'table':
            return (
                <div className="my-10 overflow-x-auto rounded-2xl border border-zinc-200">
                    <table className="w-full text-left text-sm md:text-base">
                        <thead className="bg-black text-white"><tr>{block.head.map((h) => <th key={h} className="px-4 py-3 font-bold whitespace-nowrap">{h}</th>)}</tr></thead>
                        <tbody>{block.rows.map((row, i) => <tr key={i} className="border-t border-zinc-200 even:bg-zinc-50">{row.map((cell, j) => <td key={j} className={`px-4 py-3 ${j === 0 ? 'font-bold text-black' : 'text-gray-700'}`}><RichText text={String(cell)} /></td>)}</tr>)}</tbody>
                    </table>
                </div>
            );
        case 'tip':
            return (
                <div className="my-10 flex gap-4 rounded-2xl bg-zinc-950 text-white p-6 md:p-8">
                    <Lightbulb className="w-6 h-6 shrink-0 text-yellow-300 mt-1" />
                    <p className="text-lg leading-relaxed text-zinc-200"><RichText text={block.text} /></p>
                </div>
            );
        case 'image':
            return (
                <figure className="my-12">
                    <div className="rounded-3xl bg-gradient-to-b from-zinc-50 to-zinc-100 p-8 flex items-center justify-center">
                        <img loading="lazy" decoding="async" src={block.src} alt={block.alt} className="max-h-[420px] w-auto object-contain mix-blend-multiply" />
                    </div>
                    {block.caption && <figcaption className="mt-3 text-sm text-gray-500 text-center">{block.caption}</figcaption>}
                </figure>
            );
        case 'products':
            return <ProductsBlock block={block} />;
        case 'cta':
            return (
                <div className="my-12 rounded-3xl border-2 border-black p-8 md:p-10">
                    <h3 className="text-2xl font-black uppercase italic tracking-tight mb-3">{block.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6"><RichText text={block.text} /></p>
                    {block.href?.startsWith('/')
                        ? <Link to={block.href} className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors">{block.button} <ArrowRight className="w-4 h-4" /></Link>
                        : <a href={block.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-black text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors">{block.button} <ArrowRight className="w-4 h-4" /></a>}
                </div>
            );
        default:
            return null;
    }
};

const FaqList = ({ items }) => {
    const [open, setOpen] = useState(0);
    return items.map((item, index) => (
        <div key={item.q} className="border-b border-zinc-200">
            <button type="button" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} className="w-full flex items-center justify-between gap-6 py-5 text-left">
                <h3 className="text-lg font-bold">{item.q}</h3>
                <Plus className={`w-5 h-5 shrink-0 transition-transform ${open === index ? 'rotate-45' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
                {open === index && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <p className="pb-5 text-gray-600 leading-relaxed"><RichText text={item.a} /></p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    ));
};

const formatDate = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric' });

const BlogPost = () => {
    const { slug } = useParams();
    const post = getPost(slug);
    const { scrollYProgress } = useScroll();
    const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });
    const toc = useMemo(() => (post ? post.blocks.filter((b) => b.type === 'h2') : []), [post]);

    if (!post) return <NotFound />;
    const related = (post.related || []).map(getPost).filter(Boolean);

    return (
        <main className="bg-white">
            <Helmet>
                <title>{post.metaTitle || `${post.title} | PROOMTB & ROAD`}</title>
            </Helmet>
            <motion.div style={{ scaleX: progress }} className="fixed top-0 left-0 right-0 h-1 bg-black origin-left z-[60]" />

            {/* Header */}
            <header className="relative overflow-hidden bg-zinc-950 text-white">
                <div className="absolute right-[-10%] top-[-20%] w-[60vw] h-[60vw] bg-white/5 blur-[120px] rounded-full" />
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 mb-6">
                            <Link to="/" className="hover:text-white">Inicio</Link> / <Link to="/blog" className="hover:text-white">Blog</Link> / <span className="text-zinc-300">{post.category}</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black italic tracking-tight leading-[1.05] mb-8"><TitleText text={post.title} words={2} /></h1>
                        <div className="flex flex-wrap gap-6 text-sm text-zinc-400">
                            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{formatDate(post.date)}</span>
                            <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{post.readingMinutes} min de lectura</span>
                            <span>Por el equipo de PROOMTB & ROAD</span>
                        </div>
                    </motion.div>
                    {post.cover?.src && (
                        <motion.img initial={{ opacity: 0, x: 80 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.2 }} src={post.cover.src} alt={post.cover.alt} className="w-full max-h-[420px] object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.6)]" />
                    )}
                </div>
            </header>

            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-16">
                <article className="max-w-3xl">
                    <p className="text-xl md:text-2xl leading-relaxed text-gray-800 mb-10 font-medium"><RichText text={post.intro} /></p>
                    {post.blocks.map((block, index) => <Block key={index} block={block} />)}
                    <SpecsNote className="mt-10" />

                    {post.faq?.length > 0 && (
                        <section className="mt-16">
                            <h2 className="text-3xl md:text-4xl font-black italic tracking-tight mb-6">Preguntas <span className="gradient-text-dark">frecuentes</span></h2>
                            <FaqList items={post.faq} />
                        </section>
                    )}

                    <section className="mt-16 rounded-3xl bg-zinc-950 text-white p-8 md:p-12">
                        <h2 className="text-2xl md:text-3xl font-black uppercase italic tracking-tight mb-4">¿Tienes dudas? Pasa por la tienda</h2>
                        <p className="text-zinc-400 leading-relaxed mb-8">Estamos en {seo.business.streetAddress}, {seo.business.addressLocality}. Lunes a viernes de 9 a. m. a 6 p. m. y sábados de 9 a. m. a 4 p. m.</p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <a href={`${WHATSAPP}${encodeURIComponent(`Hola, leí "${post.title}" y tengo una pregunta.`)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-colors"><MessageCircle className="w-4 h-4" /> Escríbenos por WhatsApp</a>
                            <a href={seo.business.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 border border-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-white/10 transition-colors"><MapPin className="w-4 h-4" /> Cómo llegar</a>
                        </div>
                    </section>
                </article>

                {/* Table of contents */}
                {toc.length > 2 && (
                    <aside className="hidden lg:block">
                        <div className="sticky top-28">
                            <div className="text-xs font-black uppercase tracking-[0.2em] text-gray-400 mb-4">En este artículo</div>
                            <ul className="space-y-3 border-l border-zinc-200">
                                {toc.map((h) => (
                                    <li key={h.text}><a href={`#${headingId(h.text)}`} className="block pl-4 -ml-px border-l border-transparent hover:border-black text-sm text-gray-600 hover:text-black transition-colors">{h.text}</a></li>
                                ))}
                            </ul>
                        </div>
                    </aside>
                )}
            </div>

            {related.length > 0 && (
                <section className="bg-zinc-50 py-20">
                    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                        <h2 className="text-2xl md:text-3xl font-black uppercase italic tracking-tight mb-10">Sigue leyendo</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {related.map((item) => (
                                <Link key={item.slug} to={`/blog/${item.slug}`} className="group block bg-white rounded-3xl border border-zinc-200 hover:border-black transition-colors overflow-hidden">
                                    <div className="aspect-[16/10] bg-gradient-to-b from-white to-zinc-100 flex items-center justify-center p-6">
                                        {item.cover?.src && <img loading="lazy" decoding="async" src={item.cover.src} alt={item.cover.alt} className="max-h-full max-w-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500" />}
                                    </div>
                                    <div className="p-6">
                                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-2">{item.category}</div>
                                        <h3 className="text-lg font-black italic leading-snug">{item.title}</h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </main>
    );
};

export default BlogPost;
